import { Box, Button, Typography } from '@mui/material';
import { t } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import ClockIcon from '../../../assets/icons/clock-icon.svg';
import { LANG_TEXT } from '../../../constants/language';
import { useAuthContext } from '../../../context/authContext';
import MultiOrderItem from '../components/MultiOrderItem';
import OrderItems from '../components/OrderItems';
import { Order, Restaurant } from '../order.types';

const getDeliveryDate = (order: Order): Date | null => {
  const timestamp = order.requested_delivery_time;
  if (!timestamp) return null;
  return timestamp.toDate ? timestamp.toDate() : new Date(timestamp as any);
};

const getDeliveryRequestStartMinutes = (deliveryRequest?: string) => {
  const match = deliveryRequest?.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

const getOrderDeliveryMinutes = (order: Order) => {
  const deliveryRequestStart = getDeliveryRequestStartMinutes(order.delivery_request);
  if (deliveryRequestStart !== null) return deliveryRequestStart;

  const deliveryDate = getDeliveryDate(order);
  if (!deliveryDate) return Number.MAX_SAFE_INTEGER;
  return deliveryDate.getHours() * 60 + deliveryDate.getMinutes();
};

const formatTimeFromMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0');
  return `${hours}:00`;
};

const groupOrdersByTimeSlots = (orders: Order[]): Map<string, Order[]> => {
  const grouped = new Map<string, Order[]>();

  orders.forEach((order) => {
    const deliveryMinutes = getOrderDeliveryMinutes(order);
    if (deliveryMinutes !== Number.MAX_SAFE_INTEGER) {
      const timeKey = formatTimeFromMinutes(Math.floor(deliveryMinutes / 60) * 60);

      if (!grouped.has(timeKey)) {
        grouped.set(timeKey, []);
      }
      grouped.get(timeKey)!.push(order);
    }
  });

  grouped.forEach((orders) => {
    orders.sort((a, b) => getOrderDeliveryMinutes(a) - getOrderDeliveryMinutes(b));
  });

  const sortedEntries = Array.from(grouped.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  return new Map(sortedEntries);
};

const TimeSlotGroup = ({
  timeSlot,
  orders,
  restaurantList,
  seatDataMap,
  onClick,
  handleShowInfo,
  isOnline,
}: {
  timeSlot: string;
  orders: Order[];
  restaurantList: Restaurant[];
  seatDataMap: Map<string, any>;
  onClick: (_order: Order, restaurantIds?: string[]) => void;
  handleShowInfo: (order: Order) => void;
  isOnline: boolean;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [borderHeight, setBorderHeight] = useState(0);

  useEffect(() => {
    if (!contentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setBorderHeight(entry.contentRect.height + 15);
      }
    });

    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        padding: '8px 12px 8px 26px',
      }}
    >
      <Box
        sx={{
          borderRadius: '10px 0 0 10px',
          borderTop: '2px solid var(--Gray-700, #344054)',
          borderBottom: '2px solid var(--Gray-700, #344054)',
          borderLeft: '2px solid var(--Gray-700, #344054)',
          width: '30px',
          height: borderHeight ? `${borderHeight}px` : 'auto',
          position: 'absolute',
          top: 0,
          left: 12,
        }}
      />
      <Box
        sx={{
          display: 'inline-flex',
          transform: 'rotate(90deg)',
          padding: '4px 10px',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          top: 36,
          left: '-17px',
          background: 'var(--Gray-100, #F2F4F7)',
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--Gray-700, #344054)',
        }}
      >
        {timeSlot}
      </Box>
      <Box ref={contentRef} display="flex" flexDirection="column" gap="12px">
        {orders.map((item, index) =>
          item.multi_orders && item.multi_orders.length > 1 ? (
            <MultiOrderItem
              key={index}
              restaurantList={restaurantList}
              seatDataMap={seatDataMap}
              onClickCTA={onClick}
              onShowInfo={handleShowInfo}
              item={item}
              disabled={!isOnline}
              onDamage={() => { }}
              claimed={false}
            />
          ) : (
            <OrderItems
              key={index}
              restaurantList={restaurantList}
              seatDataMap={seatDataMap}
              onClickCTA={onClick}
              onShowInfo={handleShowInfo}
              item={item}
              disabled={!isOnline}
              onDamage={() => { }}
            />
          )
        )}
      </Box>
    </Box>
  );
};

const PreOrderDrawer = ({
  preOrders,
  restaurantList,
  seatDataMap,
  onClick,
  handleShowInfo,
}: {
  preOrders: Order[];
  restaurantList: Restaurant[];
  seatDataMap: Map<string, any>;
  onClick: (_order: Order, restaurantIds?: string[]) => void;
  handleShowInfo: (order: Order) => void;
}) => {
  const { isOnline } = useAuthContext();
  const [showSidePanel, setShowSidePanel] = useState(false);
  const groupedOrders = useMemo(() => groupOrdersByTimeSlots(preOrders), [preOrders]);

  return (
    <Box
      position="absolute"
      top={0}
      right={showSidePanel ? 0 : -345}
      width={360}
      bgcolor="#F2F4F7"
      height="100%"
      zIndex={1000}
      display="flex"
      flexDirection="column"
      sx={{
        boxShadow: showSidePanel ? '0 4px 8px 3px rgba(0, 0, 0, 0.15), 0 1px 3px 0 rgba(0, 0, 0, 0.30)' : 'none',
        transition: 'right 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
      }}
    >
      <Button
        size="small"
        variant="contained"
        onClick={() => setShowSidePanel(!showSidePanel)}
        sx={{
          position: 'absolute',
          left: '-40px',
          top: '20px',
          zIndex: 9999,
          padding: '0px',
          height: '42px',
          minWidth: '42px',
          borderRadius: '8px 0 0 8px',
          background: '#F2F4F7',
          boxShadow: '-3px 2px 4px -3px rgba(0, 0, 0, 0.15), -3px 2px 4px -3px rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: '#F2F4F7',
            boxShadow: '-3px 2px 4px -3px rgba(0, 0, 0, 0.15), -3px 2px 4px -3px rgba(0, 0, 0, 0.2)',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <img src={ClockIcon} alt="toggle" />
      </Button>

      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        sx={{
          opacity: showSidePanel ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          visibility: showSidePanel ? 'visible' : 'hidden',
        }}
      >
        <Typography fontSize={24} fontWeight={500} lineHeight={'32px'} padding="16px" paddingBottom={0}>
          {t(LANG_TEXT.PRE_ORDERS)}
        </Typography>

        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          sx={{
            position: 'relative',
            overflowY: 'auto',
            my: '16px',
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {Array.from(groupedOrders.entries()).map(([timeSlot, orders]) => (
            <TimeSlotGroup
              key={timeSlot}
              timeSlot={timeSlot}
              orders={orders}
              restaurantList={restaurantList}
              seatDataMap={seatDataMap}
              onClick={onClick}
              handleShowInfo={handleShowInfo}
              isOnline={isOnline}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PreOrderDrawer;
